import { List } from 'immutable'
import { HttpResult, loadedAsyncState, loadingAsyncState, unloadedAsyncState } from 'widgets-for-react'
import { cmsPageUrl } from '../../../../utils/cmsPageUrl'
import { emptyImage, ImageBase } from '../../../../utils/image'
import { quoteSrc, quoteSrcSet } from '../../../../utils/imageSizes'
import { parsePhoneNumber } from '../../../../utils/phoneNumber'
import { getImageUrls } from '../../../../utils/sharepointImageUrls'
import { SpaarneCentrumType } from '../../../../utils/spaarneIcons'
import { getVimeoBackgroundVideoUrl } from '../../../../utils/video'
import { WaitingFor } from '../../blocks/block/waitingTimesBlock/waitingTimesBlockState'
import { Block, BlocksState } from '../../blocks/blocksState'
import {
  HomePage,
  SpaarnePage,
  DepartmentPage,
  SubdepartmentPage,
  NewsPage,
  LocationPage,
  SpecialistPage,
  EventPage,
  ResearchPage,
  WaitingTimesPage,
  CentrumPage,
  Department,
  Centrum,
  ParentPage,
  Link,
  Page,
  SubcentraPage,
  SubCentra,
  SubDepartment,
  Specialist,
  Research,
  ResearchSpecialist,
  Location,
  Author,
  FriendPage,
  FolderPage,
  DossierPage,
} from '../../cmsPageState'
import { centrumVideoAndFallbackImage } from '../../page/centrumPage/centrumPageWidget'
import { getFolderContent, getDossierNews, getDossierAgenda } from '../sharepointPageApi'
import { PageInfo, SharepointBlockData } from '../types/blockData'
import {
  HomePageData,
  SpaarnePageData,
  DepartmentPageData,
  SubdepartmentPageData,
  NewsPageData,
  LocationPageData,
  SpecialistPageData,
  EventPageData,
  WaitingTimesPageData,
  ResearchPageData,
  CentrumPageData,
  DepartmentData,
  CentrumData,
  ParentPageData,
  LinkData,
  SharepointPageData,
  SubcentraPageData,
  SubcentraData,
  SubdepartmentData,
  SpecialistData,
  ResearchData,
  LocationData,
  AuthorData,
  FriendPageData,
  FolderPageData,
  DossierPageData,
} from '../types/pageData'
import { transformSharepointBlockData } from './blockDataToBlock'
import { SearchableDossierPage } from '../../../../backendApi/generated'

const isDefined = <a>(x: a | undefined): x is a => x != undefined

export const transformToBlocks = (content: string, pageInfo?: PageInfo): BlocksState => {
  const webparts = parseWebparts(content)
  const [blocks, _] = webparts.reduce<[Block[], number]>(
    ([acc, i], webpart) => {
      const block = transformSharepointBlockData(webpart, i, pageInfo)
      if (isDefined(block)) return [[...acc, block], i + 1]
      return [acc, i]
    },
    [[], 0]
  )
  return List(blocks)
}

const parseWebparts = (canvasContent: string): SharepointBlockData[] => {
  try {
    const xmlParser = new DOMParser()
    const webparts: any[] = []
    const webpartsData = xmlParser.parseFromString(canvasContent, 'text/html')
    webpartsData.querySelectorAll('[data-sp-webpartdata]').forEach((e) => {
      const attribute = e.getAttribute('data-sp-webpartdata')
      if (attribute !== null) webparts.push(JSON.parse(attribute))
    })
    return webparts.map((data) => {
      const blocks = data.properties
      const searchablePlainTexts = data.serverProcessedContent?.searchablePlainTexts
      const searchableHtmlStrings = data.serverProcessedContent?.htmlStrings
      if (searchablePlainTexts) {
        for (const [key, value] of Object.entries(searchablePlainTexts)) {
          // Decode the html content for rich text field
          var decodedElement = document.createElement('p')
          decodedElement.innerHTML = value as string
          const decodedText = decodedElement.innerText
          blocks[key] = decodedText
        }
      }
      if (searchableHtmlStrings) {
        for (const [key, value] of Object.entries(searchableHtmlStrings)) {
          blocks[key] = value
        }
      }
      return blocks
    })
  } catch (e) {
    console.error(e)
    return []
  }
}

const getChildren = async (id: number): Promise<HttpResult<Link[]>> => {
  const res = await fetch(`/api/v1/pages/children/${id}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SpaarnePageData[] = await res.json()

  return {
    kind: 'result',
    status: 200,
    value: data.map((child) => ({
      title: child.HeaderTitle,
      url: cmsPageUrl(child),
    })),
  }
}

const getCentrumChildren = async (id: number | undefined): Promise<HttpResult<Link[]>> => {
  if (!id)
    return {
      kind: 'result',
      status: 200,
      value: [],
    }

  const res = await fetch(`/api/v1/pages/centrum-children/${id}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: LinkData[] = await res.json()

  return {
    kind: 'result',
    status: 200,
    value: data.map((item) => ({
      title: item.Title,
      url: item.Url,
    })),
  }
}

type GetWaitingTimesFor = 'department' | 'centrum'

const getHasWaitingTimes = async (
  waitingTimesFor: GetWaitingTimesFor,
  department: string
): Promise<HttpResult<boolean>> => {
  const res = await fetch(`/api/v1/waiting-times/any?title=${department}&forPage=${waitingTimesFor}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: boolean = await res.json()

  return {
    kind: 'result',
    status: 200,
    value: data,
  }
}

const transformLink = (data: LinkData | null): Link | undefined =>
  data !== null && data.Url
    ? {
        title: data.Title,
        url: data.Url,
      }
    : undefined

export const transformImage = (data: LinkData | null): ImageBase | undefined =>
  data !== null && data.Url
    ? {
        alt: data.Title,
        urls: getImageUrls(data.Url),
      }
    : undefined

export const transformHomePageData = (data: HomePageData): HomePage => ({
  kind: 'home',
  blocks: transformToBlocks(data.Content),
  url: cmsPageUrl(data),
  title: data.HeaderTitle,
  intro: data.HeaderDescription,
})

export const transformCentrumData = (data: CentrumData): Centrum => ({
  id: data.SharepointId,
  title: data.Title,
  appointmentUrl: data.AppointmentURL,
  contactItems: [
    {
      phoneNumber: data.PhoneNumber1?.PhoneNumber,
      description: data.Description1,
    },
    {
      phoneNumber: data.PhoneNumber2?.PhoneNumber,
      description: data.Description2,
    },
    {
      phoneNumber: data.PhoneNumber3?.PhoneNumber,
      description: data.Description3,
    },
    {
      phoneNumber: data.PhoneNumber4?.PhoneNumber,
      description: data.Description4,
    },
  ].filter(item => item.phoneNumber || item.description).map((item) => ({
    telephone: item.phoneNumber ? parsePhoneNumber(item.phoneNumber) : undefined,
    description: item.description,
  })),
})

export const transformCentrumPageData = (data: CentrumPageData): CentrumPage => {
  const centrum = data.Centrum
  return {
    kind: 'centrum',
    blocks: transformToBlocks(data.Content, { centrum: data.Centrum?.Title }),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    link: transformLink(data.HeaderLink),
    image: transformImage(data.HeaderImage),
    centrum: transformCentrumData(data.Centrum),
    childrenState: loadingAsyncState(() => getCentrumChildren(data.Centrum?.SharepointId)),
    videoUrl: data.Centrum ? centrumVideoAndFallbackImage(data.Centrum.Title) : unloadedAsyncState(),
    menuOpened: false,
    hasWaitingTimesState: centrum
      ? loadingAsyncState(() => getHasWaitingTimes('centrum', centrum.Title))
      : unloadedAsyncState(),
  }
}

const transformSubCentraData = (data: SubcentraData): SubCentra => ({
  id: data.SharepointId,
  title: data.Title,
  appointmentUrl: data.AppointmentURL,
  contactItems: [
    {
      phoneNumber: data.PhoneNumber1?.PhoneNumber,
      description: data.Description1,
    },
    {
      phoneNumber: data.PhoneNumber2?.PhoneNumber,
      description: data.Description2,
    },
    {
      phoneNumber: data.PhoneNumber3?.PhoneNumber,
      description: data.Description3,
    },
  ].filter(item => item.phoneNumber || item.description).map((item) => ({
    telephone: item.phoneNumber ? parsePhoneNumber(item.phoneNumber) : undefined,
    description: item.description,
  })),
  centrum: transformCentrumData(data.Centra)
})

export const transformSubCentraPageData = (data: SubcentraPageData): SubcentraPage => {
  return {
    kind: 'subcentra',
    blocks: transformToBlocks(data.Content, { subcentra: data.Subcentra?.Title }),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    link: transformLink(data.HeaderLink),
    image: transformImage(data.HeaderImage),
    subCentra: transformSubCentraData(data.Subcentra),
    childrenState: loadingAsyncState(() => getCentrumChildren(data.Subcentra?.Centra?.SharepointId)),
    menuOpened: false,
    hasWaitingTimesState:
      !data.HideWaitingTimes && data.Subcentra != null
        ? loadingAsyncState(() =>
            getHasWaitingTimes('centrum', data.Subcentra != null ? data.Subcentra.Centra.Title : '')
          )
        : unloadedAsyncState(),
  }
}

const transformDepartmentData = (data: DepartmentData): Department => ({
  id: data.SharepointId,
  title: data.Title,
  appointmentUrl: data.AppointmentURL,
  contactItems: [
    {
      phoneNumber: data.PhoneNumber1?.PhoneNumber,
      description: data.Description1,
    },
    {
      phoneNumber: data.PhoneNumber2?.PhoneNumber,
      description: data.Description2,
    },
    {
      phoneNumber: data.PhoneNumber3?.PhoneNumber,
      description: data.Description3,
    },
  ].filter(item => item.phoneNumber || item.description).map((item) => ({
    telephone: item.phoneNumber ? parsePhoneNumber(item.phoneNumber) : undefined,
    description: item.description,
  })),
  centrum: data.Centrum ? transformCentrumData(data.Centrum) : undefined,
})

export const transformFriendPageData = (data: FriendPageData): FriendPage => {
  return {
    kind: data.Path === '/vrienden' ? 'friendHome' : 'friend',
    blocks: transformToBlocks(data.Content),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    image: transformImage(data.HeaderImage),
    menuOpened: false,
    showSidemenu: data.ShowSidemenu,
  }
}

export const transformDepartmentPageData = (data: DepartmentPageData): DepartmentPage => {
  const department = data.Department
  return {
    kind: 'department',
    blocks: transformToBlocks(data.Content, { department: data.Department?.Title }),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    link: transformLink(data.HeaderLink),
    image: transformImage(data.HeaderImage),
    department: transformDepartmentData(department),
    menuOpened: false,
    hasWaitingTimesState: department
      ? loadingAsyncState(() => getHasWaitingTimes('department', department.Title))
      : unloadedAsyncState(),
  }
}

const transformSubDepartmentData = (data: SubdepartmentData): SubDepartment => ({
  id: data.SharepointId,
  title: data.Title,
  appointmentUrl: data.AppointmentURL,
  hideWaiting: data.HideWaiting,
  contactItems: [
    {
      phoneNumber: data.PhoneNumber1?.PhoneNumber,
      description: data.Description1,
    },
    {
      phoneNumber: data.PhoneNumber2?.PhoneNumber,
      description: data.Description2,
    },
    {
      phoneNumber: data.PhoneNumber3?.PhoneNumber,
      description: data.Description3,
    },
  ].filter(item => item.phoneNumber || item.description).map((item) => ({
    telephone: item.phoneNumber ? parsePhoneNumber(item.phoneNumber) : undefined,
    description: item.description,
  })),
  department: data.Department ? transformDepartmentData(data.Department) : undefined,
  centrum: data.Centrum ? transformCentrumData(data.Centrum) : undefined,
})

const shouldShowWaitingLink = (Subdepartment: SubdepartmentData | null) => !Subdepartment?.HideWaiting

export const transformSubDepartmentPageData = (data: SubdepartmentPageData): SubdepartmentPage => {
  const department = data.Subdepartment?.Department
  return {
    kind: 'subdepartment',
    blocks: transformToBlocks(data.Content, { subdepartment: data.Subdepartment?.Title }),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    link: transformLink(data.HeaderLink),
    image: transformImage(data.HeaderImage),
    subDepartment: transformSubDepartmentData(data.Subdepartment),
    hasWaitingTimesState:
      department && shouldShowWaitingLink(data.Subdepartment)
        ? loadingAsyncState(() => getHasWaitingTimes('department', department.Title))
        : unloadedAsyncState(),
  }
}

export const transformParentPageData = (data: ParentPageData): ParentPage => ({
  url: cmsPageUrl(data),
  title: data.HeaderTitle,
  ...(data.ContentType === 'Centra pagina'
    ? { kind: 'centrum', centrum: data.Centrum ? transformCentrumData(data.Centrum) : undefined }
    : { kind: 'spaarne', parent: data.Parent ? transformParentPageData(data.Parent) : undefined }),
})

export const transformSpaarnePageData = (data: SpaarnePageData): SpaarnePage => {
  const parent = data.Parent || undefined
  const grandParent = (parent?.ContentType === 'Spaarne pagina' && parent.Parent) || undefined
  return {
    kind: 'spaarne',
    blocks: transformToBlocks(data.Content),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    link: transformLink(data.HeaderLink),
    image: transformImage(data.HeaderImage),
    parent: data.Parent ? transformParentPageData(data.Parent) : undefined,
    childrenState: grandParent
      ? loadingAsyncState(() => getChildren(grandParent.SharepointId))
      : parent
      ? loadingAsyncState(() => getChildren(parent.SharepointId))
      : loadingAsyncState(() => getChildren(data.SharepointId)),
    menuOpened: false,
  }
}

export const transformEventPageData = (data: EventPageData): EventPage => ({
  kind: 'event',
  blocks: transformToBlocks(data.Content),
  url: cmsPageUrl(data),
  title: data.HeaderTitle,
  intro: data.HeaderDescription,
  link: transformLink(data.HeaderLink),
  image: transformImage(data.HeaderImage),
  date: new Date(data.EventDate),
  tags: data.Tags.map((tag) => tag.Title),
  departments: data.DepartmentsTitles,
  subDepartments: data.SubdepartmentsTitles,
  specialists: data.SpecialistsTitles,
  centra: data.CentraTitles,
  subcentra: data.SubcentraTitles,
  dossier: data.Dossier
})

const transformAuthor = (data: AuthorData): Author => ({
  url: data.Link.Url || undefined,
  name: data.Title,
  job: data.Role,
  image: transformImage(data.Image),
})

export const transformNewsPageData = (data: NewsPageData): NewsPage => ({
  kind: 'news',
  blocks: transformToBlocks(data.Content),
  url: cmsPageUrl(data),
  title: data.HeaderTitle,
  intro: data.HeaderDescription,
  link: transformLink(data.HeaderLink),
  image: transformImage(data.HeaderImage),
  date: data.PublicationDate ? new Date(data.PublicationDate) : undefined,
  author: data.Author ? transformAuthor(data.Author) : undefined,
  tags: data.Tags.map((tag) => tag.Title),
  departments: data.DepartmentsTitles,
  subDepartments: data.SubdepartmentsTitles,
  specialists: data.SpecialistsTitles,
  centra: data.CentraTitles,
  subcentra: data.SubcentraTitles,
  dossier: data.Dossier
})

const transformLocation = (data: LocationData): Location => ({
  id: data.SharepointId,
  title: data.Title,
  openingTimes: data.OpeningTimes,
  visitingTimes: data.VisitingTimes,
  openingTimesPolyclinic: data.OpeningTimesPolyclinic,
  address: data.Address,
  email: data.Email,
  postalAddress: data.PostalAddress,
  phoneNumber: data.PhoneNumber
    ? data.PhoneNumber.map((x) => ({
        description: x.Description,
        ...parsePhoneNumber(x.PhoneNumber),
      }))
    : undefined,
})

export const transformLocationPageData = (data: LocationPageData): LocationPage => ({
  kind: 'location',
  blocks: transformToBlocks(data.Content),
  url: cmsPageUrl(data),
  title: data.HeaderTitle,
  intro: data.HeaderDescription,
  image: transformImage(data.HeaderImage),
  link: transformLink(data.HeaderLink),
  location: data.Location ? transformLocation(data.Location) : undefined,
})

const transformSpecialist = (data: SpecialistData): Specialist => ({
  title: data.Title,
  academicTitle: data.AcademicTitle,
  name: data.Name,
  tussenvoegsel: data.Tussenvoegsel,
  surName: data.Surname,
  role: data.Role,
  isDoctor: data.Doctor,
  study: data.Study,
  areasOfFocus: data.AreasOfFocus,
  auxiliaryFunctions: data.AuxiliaryFunctions,
  BIGRegistrationNumber: data.BIGRegistrationNumber,
  centra: data.Departments.map((department) => department.Centrum?.Title)
    .concat(data.Centra.map((centrum) => centrum.Title))
    .reduce<SpaarneCentrumType[]>(
      (prev, curr) => prev.concat(curr === undefined || prev.includes(curr) ? [] : [curr]),
      []
    ),
  departments: data.Departments.map((department) => department.Title),
  subDepartments: data.Subdepartments.map((subDepartment) => subDepartment.Title),
})

export const transformSpecialistPageData = (data: SpecialistPageData): SpecialistPage => ({
  kind: 'specialist',
  blocks: transformToBlocks(data.Content),
  title: data.Title,
  url: cmsPageUrl(data),
  intro: data.HeaderDescription,
  image: transformImage(data.HeaderImage),
  imageOptions: {
    src: quoteSrc,
    srcset: quoteSrcSet,
    sizes: 'auto',
    lazyload: true,
    width: '300',
    height: '300',
    placeholder: '/images/placeholder--person.svg',
  },
  specialist: data.Specialist ? transformSpecialist(data.Specialist) : undefined,
})

const parseWaitingFor = (waiting?: WaitingFor) => {
  if (!waiting) {
    return
  }

  if (waiting.forPage == 'centrum') {
    return { centrum: waiting.title }
  }
  return { department: waiting.title }
}

export const transformWaitingTimesPageData = (
  data: WaitingTimesPageData,
  waitingFor?: WaitingFor
): WaitingTimesPage => ({
  kind: 'waitingTimes',
  blocks: transformToBlocks(data.Content, parseWaitingFor(waitingFor)),
  title: data.Title,
  intro: data.HeaderDescription,
  url: cmsPageUrl(data),
  image: transformImage(data.HeaderImage),
  link: transformLink(data.HeaderLink),
  waitingFor: waitingFor,
})

const transformResearchSpecialist = (data: SpecialistData): ResearchSpecialist => ({
  name: data.Name,
  role: data.Role,
  surName: data.Surname,
  tussenvoegsel: data.Tussenvoegsel,
})

const transformResearch = (data: ResearchData): Research => ({
  patientsNeeded: data.PatientNeeded,
  title: data.Title,
  specialists: data.Specialists.map(transformResearchSpecialist),
})

export const transformResearchPageData = (data: ResearchPageData): ResearchPage => ({
  kind: 'research',
  blocks: transformToBlocks(data.Content),
  url: cmsPageUrl(data),
  intro: data.HeaderDescription,
  title: data.HeaderTitle,
  link: transformLink(data.HeaderLink),
  image: transformImage(data.HeaderImage),
  research: transformResearch(data.Research),
})

export const transformFolderPageData = (data: FolderPageData): FolderPage => {
  return {
    kind: 'folderPage',
    blocks: transformToBlocks(data.Content),
    url: cmsPageUrl(data),
    title: data.HeaderTitle,
    intro: data.HeaderDescription,
    sharePointId: data.SharepointId,
    quickUtilsState: {
      isDropdownOpen: false,
      copyCompleted: false,
    },
    folderContent: loadingAsyncState(() => getFolderContent(data.FolderId)),
    lastModified: data.Modified,
    sideMenu: {
      isOpen: false,
      activeHeaderId: undefined,
    },
  }
}

export const transformDossierPageData = (data: SearchableDossierPage): DossierPage => {
  return {
    kind: 'dossierPage',
    blocks: transformToBlocks(data.Content || ""),
    url: cmsPageUrl({ ContentType: "Dossier pagina", AbsolutePath: data.AbsolutePath || "", Path: data.Path || "" }),
    title: data.HeaderTitle || "",
    intro: data.HeaderDescription || "",
    sharePointId: data.SharepointId,
    lastModified: data.Modified,
    dossierNews: loadingAsyncState(() => getDossierNews(data.Title ?? "")),
    agendaPages: loadingAsyncState(() => getDossierAgenda(data.Title ?? "")),
    quickUtilsState: {
      isDropdownOpen: false,
      copyCompleted: false,
    },
    sideMenu: {
      isOpen: false,
    },
    dossierTitle: data.Title ?? "",
    newsPage: 0,
    accumulatedNews: [],
    highlightedNews: undefined,
    totalNewsCount: 0,
    childrenState: loadingAsyncState(() => getChildren(data.SharepointId)),
    menuOpened: false,
  }
}

export const transformSharePointPageData = (data: SharepointPageData): Page => {
  switch (data.ContentType) {
    case 'Agenda pagina':
      return transformEventPageData(data)
    case 'Centra pagina':
      return transformCentrumPageData(data)
    case 'Sub-centra pagina':
      return transformSubCentraPageData(data)
    case 'Homepage':
      return transformHomePageData(data)
    case 'Spaarne pagina':
      return transformSpaarnePageData(data)
    case 'Vrienden pagina':
      return transformFriendPageData(data)
    case 'Afdeling pagina':
      return transformDepartmentPageData(data)
    case 'Sub-afdeling pagina':
      return transformSubDepartmentPageData(data)
    case 'News pagina':
      return transformNewsPageData(data)
    case 'Locatie pagina':
      return transformLocationPageData(data)
    case 'Specialist pagina':
      return transformSpecialistPageData(data)
    case 'Onderzoek pagina':
      return transformResearchPageData(data)
    case 'Wachttijden pagina':
      return transformWaitingTimesPageData(data)
    case 'Folder pagina':
      return transformFolderPageData(data)
    case 'Dossier pagina':
      // I know this is a bit of a hack, but it allows us to reuse the same transformer for both the dossier page and the searchable dossier page
      // This way we don't have to duplicate the code for transforming the dossier page.
      return transformDossierPageData(data as any as SearchableDossierPage)
  }
}
